import React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AuthLayout } from "#app/components/auth/auth-layout";
import { Alert, AlertDescription, AlertTitle, Button, Text } from "@salestrak/ui";
import { MailCircle } from "#app/components/auth/email-sent-screen";
import { useAuth } from "#app/lib/auth-context";

/**
 * Landing page for the emailed verification link. Verifies on arrival — no
 * button press. Verification only marks the address verified; it does NOT sign
 * anyone in (that would be a login-CSRF, since the link lands in whatever tab
 * opens it), so on success we route the user to sign in themselves. The token
 * is single-use, so a failed attempt (expired, already consumed — e.g. by a
 * link-scanning mail gateway) falls back to sign-in, where an unverified
 * password login re-sends a fresh link.
 */
function VerifyEmailScreen() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const { verifyEmail } = useAuth();
  const [error, setError] = React.useState(token ? "" : "This verification link is invalid.");
  const [verified, setVerified] = React.useState(false);

  // One attempt per page view: the token is consumed server-side on first
  // use, so StrictMode's double-invoked dev effect (or any re-render) must
  // not fire a second PUT that reads back as "expired".
  const attempted = React.useRef(false);
  React.useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    verifyEmail(token)
      .then(() => setVerified(true))
      .catch((err: any) => {
        setError(err.message ?? "Verification failed.");
      });
  }, [token, verifyEmail]);

  const subheading = error
    ? ""
    : verified
      ? "Your email address is verified."
      : "Confirming your email address...";

  return (
    <AuthLayout centered heading="Verify Your Email" subheading={subheading}>
      <MailCircle />

      {error ? (
        <>
          <Alert variant="error">
            <AlertTitle>Unable to verify email</AlertTitle>
            <AlertDescription>
              {error} If the link has expired, sign in with your password to
              receive a new one.
            </AlertDescription>
          </Alert>

          <Button onClick={() => navigate({ to: "/login" })}>
            Back to Sign In
          </Button>
        </>
      ) : verified ? (
        <Button onClick={() => navigate({ to: "/login" })}>
          Continue to Sign In
        </Button>
      ) : (
        <Text size="sm" variant="muted" align="center">
          One moment...
        </Text>
      )}
    </AuthLayout>
  );
}

export const Route = createFileRoute("/(auth)/verify-email")({
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  component: VerifyEmailScreen,
});
